import { reactive } from 'vue'
import { apiCall } from '../config/api.js'

// Authentication state
const authState = reactive({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
})

// Authentication actions
const authActions = {
  // Initialize auth state from localStorage
  initAuth() {
    const token = localStorage.getItem('auth_token')
    const user = localStorage.getItem('auth_user')
    
    if (token && user) {
      try {
        authState.token = token
        authState.user = JSON.parse(user)
        authState.isAuthenticated = true
        
        // Set up axios default header
        this.setAuthHeader(token)
        
        // Auth initialized from localStorage (development only)
        if (process.env.NODE_ENV === 'development') {
          console.log('Auth initialized from localStorage')
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to parse stored user data:', error)
        }
        this.logout()
      }
    }
  },

  // Login user
  async login(email) {
    authState.isLoading = true
    
    try {
      const { response, data } = await apiCall('auth/login', {
        method: 'POST',
        body: JSON.stringify({ email }),
      })
      
      if (response.ok && data.success) {
        this.setAuthData(data.token, data.user)
        return { success: true, message: data.message }
      } else {
        return { success: false, message: data.message || 'Login failed' }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Login error:', error)
      }
      return { success: false, message: 'Network error. Please try again.' }
    } finally {
      authState.isLoading = false
    }
  },

  // Set authentication data
  setAuthData(token, user) {
    authState.token = token
    authState.user = user
    authState.isAuthenticated = true
    
    // Store in localStorage
    localStorage.setItem('auth_token', token)
    localStorage.setItem('auth_user', JSON.stringify(user))
    
    // Set axios header
    this.setAuthHeader(token)
    
    // User authenticated (development only)
    if (process.env.NODE_ENV === 'development') {
      console.log('User authenticated:', user.email)
    }
  },

  // Set authorization header for API requests
  setAuthHeader(token) {
    if (window.axios) {
      window.axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    }
  },

  // Logout user
  logout() {
    authState.user = null
    authState.token = null
    authState.isAuthenticated = false
    
    // Clear localStorage
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user')
    localStorage.removeItem('tempUserId')
    localStorage.removeItem('tempUserEmail')
    localStorage.removeItem('completedUserId')
    
    // Clear axios header
    if (window.axios) {
      delete window.axios.defaults.headers.common['Authorization']
    }
    
    // User logged out (development only)
    if (process.env.NODE_ENV === 'development') {
      console.log('User logged out')
    }
  },

  // Clear auth (alias for logout for security clarity)
  clearAuth() {
    this.logout()
  },

  // Refresh token
  async refreshToken() {
    if (!authState.token) return false
    
    try {
      const { response, data } = await apiCall('auth/refresh', {
        method: 'POST',
      })
      
      if (response.ok && data.success) {
        authState.token = data.token
        localStorage.setItem('auth_token', data.token)
        this.setAuthHeader(data.token)
        return true
      } else {
        this.logout()
        return false
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Token refresh failed:', error)
      }
      this.logout()
      return false
    }
  },

  // Verify token validity
  async verifyToken() {
    if (!authState.token) return false
    
    try {
      const { response, data } = await apiCall('auth/verify', {
        method: 'POST',
      })
      
      if (response.ok && data.success) {
        return true
      } else {
        this.logout()
        return false
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Token verification failed:', error)
      }
      this.logout()
      return false
    }
  },

  // Update user profile in state
  updateUser(userData) {
    if (authState.user) {
      authState.user = { ...authState.user, ...userData }
      localStorage.setItem('auth_user', JSON.stringify(authState.user))
    }
  },

  // Get current user
  getCurrentUser() {
    return authState.user
  },

  // Check if user is authenticated
  isLoggedIn() {
    return authState.isAuthenticated && authState.token !== null
  },

  // Check if user has completed profile
  hasCompletedProfile() {
    return authState.user && authState.user.profileCompleted === true
  }
}

export { authState, authActions } 